import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { sessionApi } from '@/api/endpoints/session';
import { ApiError } from '@/api/client';
import { meApi } from '@/api/endpoints/me';
import { hexToHslTriplet } from '@/stores/entitlements';

const DEFAULT_LOGO = `${import.meta.env.BASE_URL}brand/nautmax-logo.png`;

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // White-label por subdomínio: branding público resolvido pelo Host (sem auth).
  const brandingQ = useQuery({ queryKey: ['branding'], queryFn: () => meApi.branding(), staleTime: 600_000 });
  const branding = brandingQ.data ?? null;
  const isCustom = !!branding && (!!branding.logo_url || branding.nome_exibicao !== 'NautMax');

  useEffect(() => {
    if (!branding) return;
    document.title = branding.nome_exibicao || 'NautMax';
    const root = document.documentElement;
    const brand = hexToHslTriplet(branding.cor_marca);
    const primary = hexToHslTriplet(branding.cor_primaria);
    if (brand) root.style.setProperty('--brand', brand);
    if (primary) {
      root.style.setProperty('--primary', primary);
      root.style.setProperty('--ring', primary);
    }
  }, [branding]);

  const login = useMutation({
    mutationFn: () => sessionApi.login(email, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      navigate('/dashboard', { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-4">
      <div className="flex flex-col items-center gap-3">
        {isCustom && branding?.logo_url ? (
          <img src={branding.logo_url} alt={branding.nome_exibicao} className="h-20 w-auto max-w-[80vw] object-contain" />
        ) : isCustom ? (
          <h1 className="text-3xl font-bold tracking-tight text-brand">{branding!.nome_exibicao}</h1>
        ) : (
          <img src={DEFAULT_LOGO} alt="NautMax — Monitoramento Náutico" className="h-20 w-auto max-w-[80vw] object-contain" />
        )}
        <p className="text-sm text-muted-foreground">Rastreamento náutico inteligente</p>
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Entrar</CardTitle>
          <CardDescription>Acesse com suas credenciais.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                E-mail
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {login.error && (
              <p className="text-sm text-destructive">
                {login.error instanceof ApiError && login.error.status === 401
                  ? 'E-mail ou senha inválidos.'
                  : 'Falha ao conectar. Verifique se o servidor está acessível.'}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
      <div className="max-w-sm text-center text-xs text-muted-foreground">
        <p>Problemas para acessar?</p>
        <p>Entre em contato com o suporte técnico.</p>
      </div>
    </div>
  );
}
